import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Trash2, ClipboardList, Plus, X, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ManageSurveysPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', entity_type: 'client', is_active: true, questions: [] });
  const [newQ, setNewQ] = useState({ text: '', type: 'text', required: true });
  const [respModal, setRespModal] = useState(null); // {survey, responses}
  const [newResp, setNewResp] = useState({ entity_id: '', answers: {} });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/surveys'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const addQuestion = () => {
    if (!newQ.text) return;
    setForm({ ...form, questions: [...form.questions, { ...newQ, id: Date.now() }] });
    setNewQ({ text: '', type: 'text', required: true });
  };

  const removeQuestion = (idx) => {
    setForm({ ...form, questions: form.questions.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Survey name is required'); return; }
    try { await api.post('/surveys', form); toast.success('Survey created'); setShowModal(false); setForm({ name: '', description: '', entity_type: 'client', is_active: true, questions: [] }); load(); }
    catch { toast.error('Failed'); }
  };

  const toggleActive = async (id, current) => {
    try { await api.put(`/surveys/${id}`, { is_active: !current }); load(); }
    catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this survey?')) return;
    try { await api.delete(`/surveys/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const openResponses = async (survey) => {
    try {
      const res = await api.get(`/surveys/${survey.id}/responses`);
      setRespModal({ survey: res.data.survey, responses: res.data.responses });
      setNewResp({ entity_id: '', answers: {} });
    } catch { toast.error('Failed to load responses'); }
  };

  const reloadResponses = async () => {
    if (!respModal) return;
    const res = await api.get(`/surveys/${respModal.survey.id}/responses`);
    setRespModal({ ...respModal, responses: res.data.responses });
  };

  const handleAddResponse = async () => {
    if (!newResp.entity_id) { toast.error('Entity ID is required'); return; }
    try {
      await api.post(`/surveys/${respModal.survey.id}/responses`, newResp);
      toast.success('Response submitted');
      setNewResp({ entity_id: '', answers: {} });
      reloadResponses();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteResponse = async (id) => {
    if (!window.confirm('Delete this response?')) return;
    try { await api.delete(`/surveys/${respModal.survey.id}/responses/${id}`); reloadResponses(); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <AdminSubPage title="Manage Surveys" breadcrumb={['Admin', 'System', 'Surveys']} backPath="/dashboard/admin/system" actionLabel="+ Create Survey" onAction={() => setShowModal(true)}>
      <div className="bg-white border border-gray-200" data-testid="surveys-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Survey</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Entity</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Questions</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Responses</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Active</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400"><ClipboardList className="w-8 h-8 mx-auto mb-2 text-gray-300" />No surveys created.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`survey-row-${item.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{item.name}</div><div className="text-xs text-gray-400 max-w-[200px] truncate">{item.description}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.entity_type}</td>
                <td className="px-4 py-3 text-sm text-center font-mono">{(item.questions || []).length}</td>
                <td className="px-4 py-3 text-sm text-center font-mono">{item.response_count || 0}</td>
                <td className="px-4 py-3 text-center"><Switch checked={item.is_active !== false} onCheckedChange={() => toggleActive(item.id, item.is_active)} /></td>
                <td className="px-4 py-3 flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => openResponses(item)} data-testid={`view-responses-${item.id}`}><MessageSquare className="w-3 h-3 mr-1" />Responses</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="sv-desc"><DialogHeader><DialogTitle>Create Survey</DialogTitle></DialogHeader>
          <p id="sv-desc" className="sr-only">Survey form</p>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div><Label>Survey Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="survey-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Entity Type</Label>
                <Select value={form.entity_type} onValueChange={v => setForm({...form, entity_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem><SelectItem value="loan">Loan</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem><SelectItem value="group">Group</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1"><Label>Active</Label><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /></div>
            </div>
            <div className="space-y-2">
              <Label>Questions</Label>
              <div className="flex gap-2">
                <Input placeholder="Question text" value={newQ.text} onChange={e => setNewQ({...newQ, text: e.target.value})} className="flex-1" />
                <Select value={newQ.type} onValueChange={v => setNewQ({...newQ, type: v})}>
                  <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem><SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem><SelectItem value="yes_no">Yes/No</SelectItem>
                    <SelectItem value="rating">Rating</SelectItem><SelectItem value="dropdown">Dropdown</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" size="sm" variant="outline" onClick={addQuestion}><Plus className="w-4 h-4" /></Button>
              </div>
              {form.questions.length > 0 && (
                <div className="space-y-1 mt-2">
                  {form.questions.map((q, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-1.5 border">
                      <span className="text-gray-400 text-xs w-5">{i + 1}.</span>
                      <span className="flex-1">{q.text}</span>
                      <span className="text-xs text-gray-400 capitalize">{q.type}</span>
                      <button onClick={() => removeQuestion(i)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="survey-submit-btn">Create Survey</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Responses modal */}
      <Dialog open={!!respModal} onOpenChange={(o) => !o && setRespModal(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="resp-desc">
          <DialogHeader><DialogTitle>{respModal?.survey?.name} — Responses</DialogTitle></DialogHeader>
          <p id="resp-desc" className="sr-only">Manage survey responses</p>
          {respModal && (
            <div className="space-y-4">
              <div className="bg-gray-50 border p-3">
                <p className="text-xs text-gray-500 mb-2">Submit a response on behalf of a {respModal.survey.entity_type}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input placeholder={`${respModal.survey.entity_type} ID`} value={newResp.entity_id} onChange={e => setNewResp({ ...newResp, entity_id: e.target.value })} data-testid="resp-entity-id" />
                </div>
                {(respModal.survey.questions || []).map((q) => (
                  <div key={q.id} className="mt-2 grid grid-cols-3 gap-2 items-center">
                    <Label className="text-xs">{q.text} {q.required && <span className="text-red-500">*</span>} <span className="text-gray-400">({q.type})</span></Label>
                    <Input className="col-span-2" type={q.type === 'number' ? 'number' : q.type === 'date' ? 'date' : 'text'} value={newResp.answers[q.id] || ''} onChange={e => setNewResp({ ...newResp, answers: { ...newResp.answers, [q.id]: e.target.value } })} data-testid={`resp-q-${q.id}`} />
                  </div>
                ))}
                <Button onClick={handleAddResponse} className="bg-[#1f1f1f] text-white mt-3" data-testid="resp-add-btn"><Plus className="w-4 h-4 mr-1" />Submit Response</Button>
              </div>

              <div className="border" data-testid="resp-table">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs">Entity</th>
                      {(respModal.survey.questions || []).map((q) => <th key={q.id} className="px-2 py-2 text-left text-xs">{q.text}</th>)}
                      <th className="px-2 py-2 text-left text-xs">Submitted</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {respModal.responses.length === 0 && <tr><td colSpan={3 + (respModal.survey.questions || []).length} className="px-2 py-6 text-center text-gray-400">No responses yet.</td></tr>}
                    {respModal.responses.map(r => (
                      <tr key={r.id} data-testid={`resp-rec-${r.id}`}>
                        <td className="px-2 py-2 font-mono text-xs">{r.entity_id}</td>
                        {(respModal.survey.questions || []).map((q) => <td key={q.id} className="px-2 py-2">{String(r.answers?.[q.id] ?? '-')}</td>)}
                        <td className="px-2 py-2 text-xs text-gray-500">{r.submitted_at?.slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-2 py-2"><button onClick={() => handleDeleteResponse(r.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setRespModal(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default ManageSurveysPage;
